import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { BomMaterial } from 'src/app/shared/services/bom.service';
import {
  GisOpsMapAction,
  GisOpsSurveyLrs,
  GisProjectOpsService,
  GisProjectOpsSnapshot,
} from './gis-project-ops.service';

@Component({
  selector: 'app-gis-project-mgmt-panel',
  standalone: true,
  imports: [CommonModule, RouterModule, DatePipe, CurrencyPipe],
  templateUrl: './gis-project-mgmt-panel.component.html',
  styleUrls: ['./gis-project-mgmt-panel.component.css'],
})
export class GisProjectMgmtPanelComponent implements OnChanges, OnDestroy {
  @Input() projectId: string | number | null = null;
  @Input() projectData: any = null;
  @Input() mode: 'full' | 'attendance' | 'survey' = 'full';
  @Output() mapAction = new EventEmitter<GisOpsMapAction>();

  loading = false;
  loadError = '';
  staff: any[] = [];
  wbs: any[] = [];
  surveys: any[] = [];
  attendance: any[] = [];
  machines: any[] = [];
  materials: BomMaterial[] = [];
  bomCode: string | null = null;

  private sub?: Subscription;
  private loadedFor: number | null = null;

  constructor(private ops: GisProjectOpsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId']) {
      this.loadedFor = null;
    }
    this.refresh();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get numericProjectId(): number | null {
    const id = Number(this.projectId);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  get projectName(): string {
    return this.projectData?.project_name || 'This project';
  }

  get roadLength(): string {
    const v = this.projectData?.road_length;
    if (v == null || v === '') return '';
    return `${v} km`;
  }

  get roadWidth(): string {
    const lanes = this.projectData?.lane_configuration;
    const width = this.projectData?.lane_width_adjustment;
    const parts: string[] = [];
    if (lanes) parts.push(`${lanes} lanes`);
    if (width) parts.push(`${width}m`);
    return parts.join(' · ');
  }

  get contractValue(): number | null {
    const v = Number(
      this.projectData?.budget_amount
      ?? this.projectData?.estimated_cost
      ?? this.projectData?.contract_value
    );
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  get completionPct(): number | null {
    if (this.wbs.length) {
      const sum = this.wbs.reduce((t, a) => t + (Number(a.percent_complete) || 0), 0);
      return Math.round(sum / this.wbs.length);
    }
    const v = Number(this.projectData?.percent_complete ?? this.projectData?.completion_percentage);
    return Number.isFinite(v) ? v : null;
  }

  get todayKey(): string {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }

  get todayAttendance(): any[] {
    return this.attendance.filter((row) => String(row.date || '').slice(0, 10) === this.todayKey);
  }

  get attendanceRows(): any[] {
    const today = this.todayAttendance;
    return today.length ? today : this.attendance.slice(0, 8);
  }

  get presentCount(): number {
    return this.todayAttendance.filter((row) => this.attendanceKind(row) === 'present').length;
  }

  get absentCount(): number {
    return this.todayAttendance.filter((row) => this.attendanceKind(row) === 'absent').length;
  }

  get lateCount(): number {
    return this.todayAttendance.filter((row) => this.attendanceKind(row) === 'late').length;
  }

  get geoPunchCount(): number {
    return this.ops.pointsFromAttendance(this.attendance).length;
  }

  get staffPreview(): any[] {
    return this.staff.slice(0, 6);
  }

  get wbsPreview(): any[] {
    return this.wbs.slice(0, 6);
  }

  get surveyPreview(): any[] {
    return this.surveys.slice(0, 6);
  }

  get machinePreview(): any[] {
    return this.machines.slice(0, 6);
  }

  get materialPreview(): BomMaterial[] {
    return this.materials.slice(0, 6);
  }

  get wbsAverage(): number {
    return this.completionPct ?? 0;
  }

  refresh(force = false): void {
    const id = this.numericProjectId;
    if (!id) {
      this.reset();
      return;
    }
    const cached = this.ops.getSnapshot(id);
    if (cached) this.apply(cached);
    if (!force && cached) {
      this.loading = false;
      return;
    }
    this.loading = !cached;
    this.loadError = '';
    this.sub?.unsubscribe();
    this.sub = this.ops.load(id, force).subscribe({
      next: (snap) => {
        this.apply(snap);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadError = 'Could not load project management data.';
      },
    });
  }

  staffName(row: any): string {
    return row?.full_name
      || [row?.first_name, row?.last_name].filter(Boolean).join(' ')
      || 'Staff';
  }

  staffMeta(row: any): string {
    const bits = [
      row?.designation,
      row?.employee_code,
      this.allocationLabel(row),
    ].filter(Boolean);
    return bits.join(' · ');
  }

  allocationLabel(row: any): string {
    if (!row) return '';
    if (row.allocation_type === 'full' || Number(row.allocation_percent) === 100) return 'Full';
    if (row.allocation_percent != null) return `${row.allocation_percent}%`;
    return '';
  }

  wbsTitle(row: any): string {
    return row?.activity_name || row?.wbs_name || 'Activity';
  }

  surveyTitle(row: any): string {
    return row?.survey_lead || row?.project_name || `Survey #${row?.id || ''}`;
  }

  surveyMeta(row: any): string {
    const bits = [
      this.prettyStatus(row?.status),
      this.surveyLrsLabel(row),
    ].filter(Boolean);
    return bits.join(' · ');
  }

  surveyLrsLabel(row: any): string {
    const lrs = this.ops.surveyLrs(row);
    if (!lrs) return '';
    if (lrs.chainage) return `LRS ${lrs.chainage}`;
    if (lrs.fromKm != null && lrs.toKm != null) return `LRS ${lrs.fromKm}–${lrs.toKm} km`;
    return `Route #${lrs.routeId}`;
  }

  machineTitle(row: any): string {
    return row?.machine_name || row?.title || 'Machine';
  }

  machineMeta(row: any): string {
    const bits = [this.prettyStatus(row?.status), row?.title].filter(Boolean);
    return bits.join(' · ');
  }

  materialTitle(row: BomMaterial): string {
    return row.material_name || row.material_code || 'Material';
  }

  materialMeta(row: BomMaterial): string {
    const qty = row.quantity != null ? `${row.quantity}${row.unit ? ' ' + row.unit : ''}` : '';
    return [qty, row.category].filter(Boolean).join(' · ');
  }

  attendanceName(row: any): string {
    return row?.full_name || row?.user_name || 'Staff';
  }

  attendanceKind(row: any): 'present' | 'absent' | 'late' {
    const s = String(row?.status || '').toLowerCase();
    if (s.includes('absent') || s === 'a') return 'absent';
    if (s.includes('late')) return 'late';
    return 'present';
  }

  prettyStatus(value: unknown): string {
    return String(value || '')
      .replace(/_/g, ' ')
      .trim();
  }

  showProjectOnMap(): void {
    this.mapAction.emit({ type: 'project' });
  }

  showAttendanceOnMap(): void {
    const points = this.ops.pointsFromAttendance(this.attendance);
    if (!points.length) {
      this.mapAction.emit({ type: 'project' });
      return;
    }
    this.mapAction.emit({ type: 'points', layer: 'biometric', points });
  }

  showStaffOnMap(): void {
    const points = this.ops.pointsFromStaff(this.staff);
    if (!points.length) {
      this.mapAction.emit({ type: 'project' });
      return;
    }
    this.mapAction.emit({ type: 'points', layer: 'project', points });
  }

  showMachinesOnMap(): void {
    const points = this.ops.pointsFromMachines(this.machines);
    if (!points.length) {
      this.mapAction.emit({ type: 'project' });
      return;
    }
    this.mapAction.emit({ type: 'points', layer: 'project', points });
  }

  showMaterialsOnMap(): void {
    this.mapAction.emit({ type: 'project' });
  }

  showSurveyOnMap(row?: any): void {
    if (row) {
      const lrs = this.ops.surveyLrs(row);
      if (lrs) {
        this.mapAction.emit({ type: 'survey-lrs', lrs });
        return;
      }
      const points = this.ops.pointsFromSurveys([row]);
      if (points.length) {
        this.mapAction.emit({ type: 'points', layer: 'gps', points });
        return;
      }
      this.mapAction.emit({ type: 'project' });
      return;
    }
    const lrsRows = this.surveys
      .map((s) => this.ops.surveyLrs(s))
      .filter((v): v is GisOpsSurveyLrs => !!v);
    if (lrsRows.length === 1) {
      this.mapAction.emit({ type: 'survey-lrs', lrs: lrsRows[0] });
      return;
    }
    const points = this.ops.pointsFromSurveys(this.surveys);
    if (points.length) {
      this.mapAction.emit({ type: 'points', layer: 'gps', points });
      return;
    }
    this.mapAction.emit({ type: 'project' });
  }

  private apply(snap: GisProjectOpsSnapshot): void {
    this.loadedFor = snap.projectId;
    this.staff = snap.staff;
    this.wbs = snap.wbs;
    this.surveys = snap.surveys;
    this.attendance = snap.attendance;
    this.machines = snap.machines;
    this.materials = snap.materials;
    this.bomCode = snap.bomCode;
  }

  private reset(): void {
    this.loadedFor = null;
    this.staff = [];
    this.wbs = [];
    this.surveys = [];
    this.attendance = [];
    this.machines = [];
    this.materials = [];
    this.bomCode = null;
    this.loading = false;
    this.loadError = '';
  }
}
